import type { ViewId } from "@/features/views/viewRegistry"

// ---------------------------------------------------------------------------
// View-specific element visibility config
// ---------------------------------------------------------------------------

/**
 * Controls which elements are visible in the compact task row
 * for each view. Derived from v1's CardPreview conditional rendering.
 *
 * Usage:
 * ```ts
 * const config = getViewConfig("today")
 * if (config.showDateChip) { <DateChip ... /> }
 * ```
 */
export interface ViewConfig {
  /** Show date chip (Today star, moon icon, future date, relative date) */
  showDateChip: boolean
  /** Show parent container label (project/list name) */
  showContainerLabel: boolean
  /** Show flag icon for tasks with deadlines */
  showFlag: boolean
  /** Show indicator icons (note, checklist, reminder) */
  showIcons: boolean
  /** Show task counter badge (for Project/List type objects) */
  showCounter: boolean
  /** Show avatar indicators (shared realms) */
  showAvatars: boolean
}

// ---------------------------------------------------------------------------
// Config per view — matches v1 CardPreview visibility logic
// ---------------------------------------------------------------------------

const VIEW_CONFIGS: Record<ViewId, ViewConfig> = {
  today: {
    showDateChip: false, // Already in Today — no date needed
    showContainerLabel: false,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  evening: {
    showDateChip: false, // Already in Evening — no date needed
    showContainerLabel: false,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  inbox: {
    showDateChip: true,
    showContainerLabel: false,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  upcoming: {
    showDateChip: false, // Date in section headers
    showContainerLabel: true,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  anyday: {
    showDateChip: true,
    showContainerLabel: false,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  someday: {
    showDateChip: true,
    showContainerLabel: false,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  logbook: {
    showDateChip: true, // Shows relative date (completed time)
    showContainerLabel: true,
    showFlag: false, // No flag in logbook
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  trash: {
    showDateChip: true, // Shows relative date (deleted time)
    showContainerLabel: true,
    showFlag: false, // No flag in trash
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  search: {
    showDateChip: true,
    showContainerLabel: true,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  recurring: {
    showDateChip: true,
    showContainerLabel: true,
    showFlag: false,
    showIcons: true,
    showCounter: false,
    showAvatars: false,
  },
  routines: {
    showDateChip: false,
    showContainerLabel: false,
    showFlag: false,
    showIcons: false,
    showCounter: false,
    showAvatars: false,
  },
  laterProjects: {
    showDateChip: true,
    showContainerLabel: true,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
  project: {
    showDateChip: true,
    showContainerLabel: false, // Already in the project
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: false, // v1 hides avatars in project views
  },
  list: {
    showDateChip: true,
    showContainerLabel: false,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: false,
  },
  area: {
    showDateChip: true,
    showContainerLabel: false,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: false,
  },
  tag: {
    showDateChip: true,
    showContainerLabel: true,
    showFlag: true,
    showIcons: true,
    showCounter: true,
    showAvatars: true,
  },
}

/**
 * Get the view config for a given view ID.
 * Falls back to a sensible default (everything visible) for unknown views.
 */
export function getViewConfig(viewId: ViewId): ViewConfig {
  return (
    VIEW_CONFIGS[viewId] ?? {
      showDateChip: true,
      showContainerLabel: true,
      showFlag: true,
      showIcons: true,
      showCounter: true,
      showAvatars: true,
    }
  )
}
